/**
 * Component Validation Hook
 * Handles validation logic for checklist components
 */

import { useCallback, useMemo } from "react";
import type { ChecklistComponent, ChecklistScalarValue } from "@/types/quote.types";
import { type ValidationResult, validateChecklistComponents } from "@/utils/validation";

export const useComponentValidation = (
  visibleComponents: ChecklistComponent[],
  resolvedResponses: Record<string, ChecklistScalarValue>
) => {
  const validationResults = useMemo(() => {
    if (!visibleComponents || visibleComponents.length === 0) {
      return {} as Record<string, ValidationResult>;
    }

    return validateChecklistComponents(resolvedResponses, visibleComponents);
  }, [visibleComponents, resolvedResponses]);

  const validationSummary = useMemo(() => {
    const failedFields = Object.entries(validationResults).filter(([, result]) => !result.isValid);

    if (failedFields.length === 0) {
      return { isValid: true, errors: [] as string[] };
    }

    const errors = failedFields
      .map(([fieldName, result]) => {
        const component = visibleComponents.find((comp) => comp.field_name === fieldName);
        if (!(component && result.error)) return undefined;
        return `${component.label}: ${result.error}`;
      })
      .filter((message): message is string => Boolean(message));

    return { isValid: false, errors };
  }, [visibleComponents, validationResults]);

  const hasValue = useCallback(
    (component: ChecklistComponent): boolean => {
      const rawValue = resolvedResponses[component.field_name];
      if (rawValue === undefined || rawValue === null) {
        return false;
      }

      if (Array.isArray(rawValue)) {
        return rawValue.length > 0;
      }

      if (typeof rawValue === "string") {
        return rawValue.trim() !== "";
      }

      return true;
    },
    [resolvedResponses]
  );

  return {
    validationResults,
    validationSummary,
    hasValue,
  };
};
